"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Notifier = void 0;
const queue_microtask_1 = __importDefault(require("queue-microtask"));
const error_1 = require("../error");
/**
 * Invokes callbacks for a single notification event. Maintains a queue of
 * {@link Observers} which will be notified.
 *
 * Mainly responsible for catching recursive observer chains, i.e., when
 * notifying a callback results in that same callback being notified again.
 */
class NotificationJob {
    constructor(type) {
        /**
         * {@link Observers} which are currently queued up for notification.
         */
        this.queue = new Set();
        /**
         * All {@link Observers} which have been notified so far in the job.
         *
         * This is used to catch recursive observer chains.
         */
        this.trace = new Set();
        this.type = type;
    }
    run() {
        this.notify(true);
    }
    /**
     * Add {@link Observers} to be notified in this job.
     *
     * @throws if `observers` was already notified by this job.
     */
    enqueue(observers) {
        /*
            Catch a recursive observer chain. As Observers are notified, they are
            added to `trace`. If `observers` exists in `trace`, we know that it has
            already been notified by this job, and that it was enqueued in response to
            a callback being notified.
        */
        if (this.trace.has(observers)) {
            const traceString = [...this.trace, observers]
                .map(observer => `"${observer.name}"`)
                .join(' -> ');
            throw new error_1.EcceError('Recursive observers: ' + traceString);
        }
        this.queue.add(observers);
        return this;
    }
    /**
     * Notify all {@link Observers} enqueued in the job.
     * @param root `true` if this is the root notification for enqueued Observers,
     * 	or `false` if we are recursively notifying a callback chain.
     */
    notify(root) {
        const observersToNotify = [...this.queue];
        this.queue.clear();
        for (const observers of observersToNotify) {
            this.trace.add(observers);
            for (const callback of this.callbacksToNotify(observers, root)) {
                callback();
                // Notify any chained observers immediately.
                if (this.queue.size) {
                    this.notify(false);
                }
            }
        }
    }
    /**
     * Get `observers`' callbacks which should be invoked for `observers` in a
     * given notification pass.
     *
     * If this is a `root` notification of an enqueued Observer, only notify the
     * callbacks of `this.type`. If we are recursively notifying an observer
     * chain, notify _all_ callbacks, regardless of type, as there is no 'latent'
     * time at that point.
     */
    callbacksToNotify(observers, root) {
        return root
            ? observers.callbacksOfType(this.type)
            : observers.allCallbacks();
    }
}
/**
 * The global notification manager.
 */
class Notifier {
    static get instance() {
        return this.INSTANCE;
    }
    constructor() {
        this.activeJob = null;
        this.latentJob = null;
    }
    /**
     * Notify an `observer`'s callbacks. `immediate` callbacks will be invoked
     * immediately, and `latent` observers will be enqueued to be notified at the
     * end of the current event loop tick.
     */
    notify(observers) {
        if (this.activeJob) {
            this.activeJob.enqueue(observers);
            return;
        }
        if (observers.hasCallbacksOfType('immediate')) {
            this.runJob(new NotificationJob('immediate').enqueue(observers));
        }
        if (this.latentJob) {
            this.latentJob.enqueue(observers);
            return;
        }
        const latentJob = new NotificationJob('latent').enqueue(observers);
        this.latentJob = latentJob;
        (0, queue_microtask_1.default)(() => {
            try {
                this.runJob(latentJob);
            }
            finally {
                this.latentJob = null;
            }
        });
    }
    runJob(job) {
        try {
            this.activeJob = job;
            job.run();
        }
        finally {
            this.activeJob = null;
        }
    }
}
exports.Notifier = Notifier;
Notifier.INSTANCE = new Notifier();
//# sourceMappingURL=notifier.js.map